import { NextRouter } from 'next/router';

export const FUNCTIONAL_QUERY_PARAMS = ['searchTerm', 'page', 'orderBy', 'params', 'isPreview', 'culture'];

/**
 * Sets values from router query parameters
 * @param {*} nuxt Nuxt context 'this.$nuxt'
 * @param  {...any} args Objects to set values
 */

export const assignValuesFromRouteQuery = (route: Record<string, any>, ...args: Record<string, any>[]) => {
  if (route.query && args.length > 0) {
    args.forEach((argElement: any) => {
      if (typeof argElement === 'object') {
        Object.entries(argElement).forEach(([key, value]) => {
          argElement[key] = Array.isArray(value) ? [] : null;
        });

        Object.entries(route.query).forEach(([key, value]) => {
          if (Object.prototype.hasOwnProperty.call(argElement, key)) {
            if (Array.isArray(argElement[key]) && !Array.isArray(value)) {
              argElement[key].push(value);
            } else {
              argElement[key] = value;
            }
          }
        });
      } else {
        console.error(`Unsupported function argument! Arguments can only be objects, "${typeof argElement}" given!`);
      }
    });
  }
};

/**
 * Sets values to router query parameters
 * @param {*} nuxt Nuxt context 'router'
 * @param {*} data values object
 */
export const pushValuesToRouteQuery = (router: any, data: object) => {
  const currentQuery = { ...router.query };
  delete currentQuery.params;
  replaceValuesOfRouteQuery(router, {
    ...currentQuery,
    ...data,
  });
};

/**
 * Replaces values of the router query
 * @param {*} router Next context 'router'
 * @param {*} data values object
 */
export const replaceValuesOfRouteQuery = (router: any, data: object) => {
  const pathname =
    router.asPath.indexOf('?') > 0 ? router.asPath.substring(0, router.asPath.indexOf('?')) : router.asPath;
  cleanQueryData(data);
  router.replace(
    {
      pathname,
      query: data,
    },
    null,
    { shallow: true },
  );
};

export const cleanQueryData = (data: Record<string, any>) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == null || data[key] === '' || (Array.isArray(data[key]) && data[key].length === 0)) {
      delete data[key];
    }
  });
};

export const getSearchFilters = (route: any, nameMap: { [key: string]: string }) => {
  const filters: any[] = [];
  Object.entries(route.query).forEach(([key, value]) => {
    if (!FUNCTIONAL_QUERY_PARAMS.includes(key)) {
      const filterName = nameMap ? nameMap[key] : key;
      if (filterName) {
        filters.push(createFilter(filterName, Array.isArray(value) ? value : [value]));
      }
    }
  });

  return filters;
};

export type SearchFiltersMapConfig = { [key: string]: { name: string; relation?: string; operator?: string } };

export const getSearchFiltersByMapConfig = (route: any, nameMap: SearchFiltersMapConfig) => {
  const filters: any[] = [];
  Object.entries(route.query).forEach(([key, value]) => {
    if (!FUNCTIONAL_QUERY_PARAMS.includes(key)) {
      const filterName = nameMap ? nameMap[key]?.name : key;
      const relation = nameMap ? nameMap[key]?.relation || 'eq' : 'eq';
      const operator = nameMap ? nameMap[key]?.operator || 'and' : 'and';
      filters.push(createFilter(filterName, Array.isArray(value) ? value : [value], relation, operator));
    }
  });

  return filters;
};

const createFilter = (name: string, value: any, relation = 'eq', operator = 'and') => {
  return {
    operator,
    name,
    relation,
    value,
  };
};

export const countFilters = (router: NextRouter) => {
  let result = 0;
  if (router && router.query) {
    Object.entries(router.query).forEach(([key, value]) => {
      if (!FUNCTIONAL_QUERY_PARAMS.includes(key)) {
        if (Array.isArray(value)) {
          result += value.length;
        } else {
          result++;
        }
      }
    });
  }
  return result;
};
